import { ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getJwtToken } from './preferencesClient';
import { getMidwayIdToken } from './midwayToken';

/**
 * Header the backend (LambdaMain) reads the Midway id_token from, to mint a TransitiveAuth token
 * carrying the caller's employee identity for TRANSCOM. See src/api/midwayToken.ts.
 */
export const CLIENT_ID_TOKEN_HEADER = 'X-Client-IDToken';

/**
 * Operations whose AppSync resolvers forward {@link CLIENT_ID_TOKEN_HEADER} to the backend — the
 * only ones for which obtaining and sending the token accomplishes anything. Names are the GraphQL
 * operation names as generated in src/generated/graphql.tsx.
 *
 * Extending employee attribution to another operation (a future `CreateStringGroup`, say) takes two
 * edits, in two packages: the operation's resolver in LangHubCompositeServiceSchema has to pass the
 * header through, and the operation has to be listed here. That coupling is deliberate — keeping
 * this list a mirror of the resolvers rather than "every mutation" is what stops the app awaiting
 * Midway and shipping a bearer id_token for an operation that drops it.
 *
 * {@link authLink} below is the only production consumer; the export exists so the tripwire test in
 * test/unit-tests/api/authLink.test.ts can check these names against the generated documents, which
 * is what would otherwise make a rename of the operation a silent loss of employee attribution.
 */
export const EMPLOYEE_IDENTITY_OPERATIONS: ReadonlySet<string> = new Set([
  'SubmitStringGroupTranslation',
  'CancelStringGroupTranslation',
]);

/**
 * Attaches auth headers to every AppSync operation: the Cognito JWT that authorizes the request,
 * plus — for the {@link EMPLOYEE_IDENTITY_OPERATIONS} only, and only when available — the Midway
 * id_token the backend exchanges for an employee TA token.
 *
 * Restricted to those operations because they are the only ones whose resolvers forward the header
 * and thus the only ones that reach TRANSCOM under the caller's identity. Everything else would pay
 * a Midway round-trip to send a bearer id_token that AppSync discards, so the narrower gate both
 * removes that cost and keeps the token off traffic that has no use for it (defense in depth should
 * AppSync request logging ever be enabled).
 *
 * Uses the async `setContext` link because the id_token comes from a `fetch`; that await is bounded
 * and costs one Midway round-trip per token lifetime, not per request (see src/api/midwayToken.ts).
 */
export const authLink: ApolloLink = setContext(
  async (operation, { headers = {} }) => {
    // Read the JWT before awaiting Midway, so a slow Midway request cannot let the session token
    // lapse between authorizing this operation and sending it.
    const authorization = getJwtToken() || '';
    // `operationName` is the one mechanism used to identify the operation: Apollo derives it from
    // the document's operation definition before any link runs (ApolloLink.execute →
    // transformOperation), so reading it is equivalent to walking `query.definitions` here. It is
    // absent only for an anonymous operation, which cannot be one of the named operations above.
    const operationName = operation.operationName ?? '';
    const midwayIdToken = EMPLOYEE_IDENTITY_OPERATIONS.has(operationName)
      ? await getMidwayIdToken()
      : null;

    return {
      headers: {
        ...headers,
        authorization,
        // Omitted rather than sent empty when unavailable: the backend treats a missing header as
        // "no employee identity" and falls back, whereas an empty value is a token that fails
        // validation.
        ...(midwayIdToken ? { [CLIENT_ID_TOKEN_HEADER]: midwayIdToken } : {}),
      },
    };
  },
);
